import React, { useState } from 'react';

import { useLocation } from 'react-router-dom';

import {
  SideNavTabContextMenu,
  titleForPath,
  useWorkspaceNav,
  type ContextMenuItem,
} from '@features/workspace';
import { Icons, type DomainKey } from '@shared/components/ui';
import { destinationForPath, isAtOrUnder } from '@shared/navigation/destinations';

import CollapsedNavBadge from './CollapsedNavBadge';
import CollapsedRowTooltip from './CollapsedRowTooltip';
import SideNavRowButton from './SideNavRowButton';
import SideNavRowLink from './SideNavRowLink';

interface SideNavItemProps {
  id: string;
  label: string;
  icon: keyof typeof Icons;
  /** When set, render the duotone domain-colored icon. */
  domain?: DomainKey;
  path?: string;
  /**
   * Path used ONLY to compute the active/selected highlight, for rows that
   * navigate via `onClick` rather than a link.
   * Ignored when `path` is set.
   */
  activePath?: string;
  /** Match the active path exactly (===) instead of the default prefix match.
   * Needed for sibling routes like /chat vs /chat/new. */
  exact?: boolean;
  isCollapsed: boolean;
  onClick?: () => void;
  count?: number | null;
  /** Row-specific actions appended to the right-click menu (e.g. Move up / Unpin). */
  contextMenuItems?: ContextMenuItem[];
  /** Leave room at the right edge for a control overlaid on the row (a parent's chevron). */
  rightInset?: boolean;
  /**
   * Destination ids this row also lights for: a parent's sub-items while they
   * are hidden (icon-only rail or folded), so the current page always has a
   * lit row to point at.
   */
  activeFor?: string[];
}

/**
 * Whether the row for `path` is the current location's row.
 *
 * A catalog destination is active only when it is the location's
 * longest-matching destination — the rule the mobile bar uses — so Inbox
 * (`/attention`) is not also lit on its children Starred and Reading Room.
 */
const isPathActive = (
  locationPathname: string,
  path: string | undefined,
  exact = false
): boolean => {
  if (!path) return false;
  const pathWithoutQuery = path.split('?')[0] ?? '';
  if (exact) return locationPathname === pathWithoutQuery;
  if (pathWithoutQuery === '/') return locationPathname === '/';
  const dest = destinationForPath(pathWithoutQuery);
  if (dest) return destinationForPath(locationPathname)?.id === dest.id;
  return isAtOrUnder(locationPathname, pathWithoutQuery);
};

/** Whether the current location is one of `ids`' destinations. */
const isLocationAmong = (locationPathname: string, ids: string[] | undefined): boolean => {
  if (!ids?.length) return false;
  const current = destinationForPath(locationPathname)?.id;
  return current !== undefined && ids.includes(current);
};

/** Whether the row is lit: its own path matches, or the location is one it stands in for. */
const isRowActive = (
  locationPathname: string,
  row: {
    path: string | undefined;
    activePath: string | undefined;
    exact: boolean | undefined;
    activeFor: string[] | undefined;
  }
): boolean =>
  isPathActive(locationPathname, row.path ?? row.activePath, row.exact) ||
  isLocationAmong(locationPathname, row.activeFor);

const formatDisplayLabel = (label: string, count: number | null | undefined): string => {
  return count != null ? `${label} (${count})` : label;
};

const resolveCollapsedBadge = (
  isCollapsed: boolean,
  count: number | null | undefined
): number | null => (isCollapsed && count != null && count > 0 ? count : null);

/** Count to show as the expanded trailing badge, or null to hide it (zero/empty). */
const resolveExpandedCount = (
  isCollapsed: boolean,
  count: number | null | undefined
): number | null => (!isCollapsed && count != null && count > 0 ? count : null);

/** Optional row props shared by the link and button branches (aria-label/badge/trailing). */
interface OptionalRowProps {
  ariaLabel?: string;
  collapsedBadge?: React.ReactNode;
  trailing?: React.ReactNode;
}

/**
 * Build the conditionally-present row props in one place so the JSX branches
 * stay flat — keeps the component's cyclomatic complexity within limits.
 */
const buildOptionalRowProps = (
  label: string,
  isCollapsed: boolean,
  count: number | null | undefined
): OptionalRowProps => {
  const collapsedBadgeCount = resolveCollapsedBadge(isCollapsed, count);
  const expandedCount = resolveExpandedCount(isCollapsed, count);
  const props: OptionalRowProps = {};
  if (isCollapsed) props.ariaLabel = formatDisplayLabel(label, collapsedBadgeCount);
  if (collapsedBadgeCount != null) {
    props.collapsedBadge = <CollapsedNavBadge count={collapsedBadgeCount} />;
  }
  if (expandedCount != null) {
    props.trailing = (
      <span className="text-sidebar-foreground text-xs font-semibold">{expandedCount}</span>
    );
  }
  return props;
};

const SideNavItem: React.FC<SideNavItemProps> = ({
  id: _id,
  label,
  icon,
  domain,
  path,
  activePath,
  exact,
  isCollapsed,
  onClick,
  count,
  contextMenuItems,
  rightInset,
  activeFor,
}) => {
  const location = useLocation();
  const isActive = isRowActive(location.pathname, { path, activePath, exact, activeFor });

  const { openPath, openDestination } = useWorkspaceNav();
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);

  const handleContextMenu = (e: React.MouseEvent) => {
    if (!path) return;
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  };

  const handleClick = (e: React.MouseEvent) => {
    if (path) {
      e.preventDefault();
      // A catalog destination opens-or-focuses its tab at wherever it was left;
      // anything else opens-or-reuses the tab for its pathname.
      const dest = destinationForPath(path);
      if (dest) openDestination(dest.id);
      else openPath(titleForPath(path), path);
    }
    onClick?.();
  };

  const optionalProps = buildOptionalRowProps(label, isCollapsed, count);
  const tooltipLabel = optionalProps.ariaLabel ?? label;

  if (path) {
    return (
      <>
        <CollapsedRowTooltip isCollapsed={isCollapsed} label={tooltipLabel}>
          <SideNavRowLink
            to={path}
            isCollapsed={isCollapsed}
            icon={icon}
            {...(domain ? { domain } : {})}
            label={label}
            active={isActive}
            onClick={handleClick}
            onContextMenu={handleContextMenu}
            {...(rightInset && { rightInset })}
            {...optionalProps}
          />
        </CollapsedRowTooltip>
        {contextMenu && (
          <SideNavTabContextMenu
            title={titleForPath(path)}
            path={path}
            x={contextMenu.x}
            y={contextMenu.y}
            onClose={() => setContextMenu(null)}
            {...(contextMenuItems && { extraItems: contextMenuItems })}
          />
        )}
      </>
    );
  }

  return (
    <CollapsedRowTooltip isCollapsed={isCollapsed} label={tooltipLabel}>
      <SideNavRowButton
        isCollapsed={isCollapsed}
        icon={icon}
        {...(domain ? { domain } : {})}
        label={label}
        active={isActive}
        hover="hover:text-sidebar-foreground"
        {...(onClick ? { onClick } : {})}
        {...optionalProps}
      />
    </CollapsedRowTooltip>
  );
};

export default SideNavItem;
